import React, { useEffect, useState } from "react";
import { Typography, List, Flex, Checkbox, Alert } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getNotifications, markNotificationAsRead, notifyMarkAsRead, notifyMarkAllAsRead, markAllAsRead, deleteNotifications } from "../../redux/slices/NotifySlice";
import CustomButton from "../../components/CustomButton/Button";
import { BTN_TXT } from "../../constants/constants";
import { formatTimeAgo } from "../../helpers/helpers";
import InfoModal from "../../components/CustomModal/InfoModal"
import DangerButton from "../../components/CustomButton/DangerButton";
import "./Notifications.scss";
import CancelButton from "../../components/CustomButton/CancelButton";

const { Title, Text, Link } = Typography;

const Notifications = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { notifyList, totalCount, unreadCount, loading, markAllAsReadLoading, deleteNotificationsLoading, error } = useSelector((state) => state.notification);
    const [deleteMode, setDeleteMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // null = bulk delete, id = single delete
    const [page, setPage] = useState(1);
    const limit = 10;

    useEffect(() => {
        dispatch(getNotifications({ page, limit }));
    }, [dispatch, page]);

    const markAsRead = async (notification) => {
        if (notification.isRead) return;
        const res = await dispatch(notifyMarkAsRead({ notificationId: notification.notificationId, payload: { isRead: true } }));
        if (res?.payload?.statusCode === 200) {
            dispatch(markNotificationAsRead(notification.notificationId));
        }

    };

    const handleMarkAllAsRead = async () => {
        const res = await dispatch(notifyMarkAllAsRead());
        if (res?.payload?.statusCode === 200) {
            dispatch(markAllAsRead());
        }
    }

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;

        const res = await dispatch(deleteNotifications({ ids: selectedIds }));
        if (res?.payload?.statusCode === 200) {
            setSelectedIds([]);
            setPage(1);  
            dispatch(getNotifications({ page: 1, limit }));
        }
    };

    const handleSingleDelete = async (id) => {
        const res = await dispatch(deleteNotifications({ ids: [id] }));
        if (res?.payload?.statusCode === 200) {
            setSelectedIds(selectedIds.filter(x => x !== id));
            setPage(1);  
            dispatch(getNotifications({ page: 1, limit }));
        }
    };

    return (
        <div className="notifications-container">
            <Flex justify="space-between" align="flex-start">
                <Title level={3}>{`Notifications (${totalCount})`}</Title>

                <Flex gap={10}>
                    {unreadCount > 0 && (
                        <CustomButton
                            onClick={handleMarkAllAsRead}
                            loading={markAllAsReadLoading}
                            variant="outlined"
                            color="primary"
                        >
                            {`Mark all as read (${unreadCount})`}
                        </CustomButton>
                    )}

                    {/* NEW - CLEAR MODE BUTTON */}
                    <CustomButton
                        variant="outlined"
                        color={deleteMode ? "primary" : "danger"}
                        onClick={() => {
                            setDeleteMode(!deleteMode);
                            setSelectedIds([]);
                        }}
                    >
                        {deleteMode ? "Cancel" : "Clear"}
                    </CustomButton>
                </Flex>
            </Flex>

            {deleteMode && (
                <Flex justify="space-between" style={{ margin: "12px 0" }}>

                    {/* SELECT ALL TOGGLE BUTTON */}
                    <CustomButton
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            if (selectedIds.length === notifyList.length) {
                                setSelectedIds([]);
                            } else {
                                setSelectedIds(notifyList.map(n => n.notificationId));
                            }
                        }}
                    >
                        {selectedIds.length === notifyList.length
                            ? "Unselect All"
                            : "Select All"}
                    </CustomButton>

                    {/* DELETE SELECTED BUTTON */}
                    <CustomButton
                        variant="filled"
                        color="danger"
                        disabled={selectedIds.length === 0}
                        onClick={() => setShowDeleteModal(true)}
                    >
                        Clear Selected ({selectedIds.length})
                    </CustomButton>
                </Flex>
            )}

            <List
                itemLayout="horizontal"
                dataSource={notifyList}
                loading={loading}
                renderItem={(item) => (
                    <List.Item
                        style={{
                            backgroundColor: item.isRead ? "" : "#F5F8FF",
                            transition: "0.2s",
                            cursor: "pointer",
                        }}
                        onClick={() => !deleteMode && markAsRead(item)}
                    >
                        <Flex align="center" gap={12} style={{ width: "100%" }}>

                            {deleteMode && (
                                <Checkbox
                                    checked={selectedIds.includes(item.notificationId)}
                                    onChange={(e) => {
                                        const checked = e.target.checked;

                                        setSelectedIds((prev) =>
                                            checked
                                                ? [...prev, item.notificationId]
                                                : prev.filter((id) => id !== item.notificationId)
                                        );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            )}

                            <List.Item.Meta
                                title={<Text style={{ fontSize: 13, fontWeight: 500 }}>{item.message}</Text>}
                                description={
                                    item.redirectUrl && !deleteMode ? (
                                        <Text style={{ fontSize: 12, color: "#768EA7" }}>
                                            <Link
                                                style={{ fontSize: 12, textDecoration: "underline" }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    markAsRead(item);
                                                    navigate(item.redirectUrl);
                                                }}
                                            >
                                                {BTN_TXT.CLICK_HERE}
                                            </Link>
                                            {` ${BTN_TXT.TO_VIEW_DETAILS}`}
                                        </Text>
                                    ) : (
                                        <Text style={{ fontSize: 12 }}>{item.description}</Text>
                                    )
                                }
                            />

                            {/* DELETE ICON (single delete) */}
                            {deleteMode && (
                                <CustomButton
                                    variant="text"
                                    color="danger"
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteTarget(item.notificationId); // target this notification
                                        setShowDeleteModal(true);
                                    }}
                                >
                                    Clear
                                </CustomButton>

                            )}

                            {/* TIME */}
                            {!deleteMode && (
                                <Text style={{ color: "#768EA7", fontSize: 10 }}>
                                    {formatTimeAgo(item.createdAt)}
                                </Text>
                            )}
                        </Flex>
                    </List.Item>

                )}
            />

            {notifyList.length < totalCount && (
                <Flex justify="center" style={{ marginTop: 16 }}>
                    <CustomButton
                        color="primary"
                        loading={loading}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        {BTN_TXT.MORE}
                    </CustomButton>
                </Flex>
            )}
            {showDeleteModal && (
                <InfoModal
                    title={<Title level={5}>Clear Notifications</Title>}
                    open
                    centered
                    onCancel={() => {
                        setShowDeleteModal(false);
                        setDeleteTarget(null);
                    }}
                    content={
                        <>
                            <Text>
                                {deleteTarget ? (
                                    'Are you sure you want to clear this notification? Please confirm to continue.'
                                ) : (
                                    'Are you sure you want to clear all selected notifications? Please confirm to continue.'
                                )}
                            </Text>
                            {error && (
                                <Alert style={{ marginTop: 10 }} message={error} type="error" showIcon />
                            )}

                            <Flex justify="end" gap={10} style={{ marginTop: 20 }}>
                                <CancelButton
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setDeleteTarget(null);
                                    }}
                                />
                                <DangerButton
                                    onClick={async () => {
                                        if (deleteTarget) {
                                            // Single delete
                                            await handleSingleDelete(deleteTarget);
                                        } else {
                                            // Bulk delete
                                            await handleBulkDelete();
                                        }
                                        setShowDeleteModal(false);
                                        setDeleteTarget(null);
                                    }}
                                    loading={deleteNotificationsLoading}
                                    isSolid={true}
                                >
                                    {BTN_TXT.DELETE}
                                </DangerButton>
                            </Flex>
                        </>
                    }
                />
            )}
        </div>
    );
};

export default Notifications;
